import { darken } from 'polished'
import React from 'react'
import styled from 'styled-components'
import { ThemedText } from 'theme/components'
import { flexRowNoWrap } from 'theme/styles'
import { HoneypotDetails } from '../../honeypot/honeypotService'

interface TokenHoneypotDetailsProps {
  honeypotDetails: HoneypotDetails | null
  isInputToken: boolean
}

const LabelRow = styled.div`
  ${flexRowNoWrap};
  align-items: center;
  color: ${({ theme }) => theme.neutral2};
  font-size: 0.75rem;
  line-height: 1rem;
  margin-bottom: 6px;
  width: 100%;

  span:hover {
    cursor: pointer;
    color: ${({ theme }) => darken(0.2, theme.neutral2)};
  }
`

const HoneypotDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: transparent;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
`

const DetailsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`

const DetailGroupLeft = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

const DetailGroupRight = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-left: auto;
`

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`

const Dot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`

const TaxText = styled(ThemedText.BodySmall)<{ isHighTax: boolean }>`
  color: ${({ isHighTax, theme }) => (isHighTax ? theme.critical : theme.text)};
`

const TokenHoneypotDetails: React.FC<TokenHoneypotDetailsProps> = ({ honeypotDetails, isInputToken }) => {
  if (!honeypotDetails) {
    return null
  }

  const riskLevel = honeypotDetails.summary?.riskLevel ?? 0
  const isHoneypot = honeypotDetails.honeypotResult?.isHoneypot
  const getColorForHoneypot = isHoneypot ? 'red' : 'green'

  // Round the tax values to the nearest whole number
  const buyTax =
    honeypotDetails.simulationResult?.buyTax !== undefined ? Math.round(honeypotDetails.simulationResult.buyTax) : 'N/A'
  const sellTax =
    honeypotDetails.simulationResult?.sellTax !== undefined
      ? Math.round(honeypotDetails.simulationResult.sellTax)
      : 'N/A'
  const transferTax =
    honeypotDetails.simulationResult?.transferTax !== undefined
      ? Math.round(honeypotDetails.simulationResult.transferTax)
      : 'N/A'

  return (
    <HoneypotDetailsContainer>
      <LabelRow>Token Security Simulation</LabelRow>
      <DetailsRow>
        <DetailGroupLeft>
          <ThemedText.BodyPrimary>Tax:</ThemedText.BodyPrimary>
          <DetailItem>
            <TaxText isHighTax={(buyTax as number) > 9}>B: {buyTax}%</TaxText>
          </DetailItem>
          <DetailItem>
            <TaxText isHighTax={(sellTax as number) > 9}>S: {sellTax}%</TaxText>
          </DetailItem>
          <DetailItem>
            <TaxText isHighTax={(transferTax as number) > 9}>T: {transferTax}%</TaxText>
          </DetailItem>
        </DetailGroupLeft>
        <DetailGroupRight>
          <DetailItem>
            <ThemedText.BodySmall>Honeypot:</ThemedText.BodySmall>
            <Dot color={getColorForHoneypot} />
          </DetailItem>
          <DetailItem>
            <ThemedText.BodySmall>Safety:</ThemedText.BodySmall>
            <Dot color={riskLevel === 1 ? 'green' : riskLevel === 100 ? 'red' : 'yellow'} />
          </DetailItem>
        </DetailGroupRight>
      </DetailsRow>
    </HoneypotDetailsContainer>
  )
}

export default TokenHoneypotDetails
